/**
 * app.ts — Rad1o Svelte entry point
 *
 * Imports vanilla JS modules in dependency order (side-effect imports that
 * set window globals), then mounts the root Svelte component.
 *
 * Import order is load-order critical:
 *   scaler → logger → m1 → m2 → m3 → m4 → m6 → m7 → m8 → main
 *
 * After all modules initialize, the Svelte App component replaces M5's mount().
 */

// ── Vanilla JS modules (side-effect imports — set window globals) ──
import '../modules/scaler.js';
import '../modules/logger.js';
import '../modules/m1-audio-engine.js';
import '../modules/m2-station-store.js';
import '../modules/m3-playback-controller.js';
import '../modules/m4-recently-played-log.js';
import '../modules/m6-metadata-engine.js';
import '../modules/m7-station-browser.js';
import '../modules/m8-media-session-bridge.js';

import App from './app.svelte';
import { M1, M3, M4, M6, M7, M8, Logger } from './modules';

// ── Initialize non-UI modules ──────────────────────────────────
// Replicates the bootstrap logic from main.js

const apiBaseUrl = window.location.protocol !== 'file:'
  ? 'https://api.rad1o.app'
  : null;

M6?.init?.(apiBaseUrl as any);

if (M1?.init) {
  M1.init({ apiBase: apiBaseUrl });
}

// Pre-load station catalog (non-blocking)
let catalogError: string | null = null;
M7?.init?.()
  .then(() => { catalogError = null; })
  .catch((e: any) => {
    catalogError = e?.message || 'Failed to load station catalog';
    Logger?.log?.({ ts: Date.now(), module: 'main', fn: 'M7.init', inputs: {}, output: null, durationMs: 0, error: e });
  });

// Initialize M3 (restores selection WITHOUT starting audio)
M3?.init?.();

// ── First-gesture listener (browser autoplay policy) ──────────
// MUST be registered BEFORE M8.init() so it fires first during the
// gesture. This ensures M3.resumePlayback() (which now skips debounce)
// creates the audio element BEFORE M8's iOS activation handler runs.
// M8 needs _audio to exist for its pause→play cycle to associate
// handlers with the session; otherwise iOS defaults to seek buttons.

let firstGestureDone = false;
function onFirstGesture() {
  if (firstGestureDone) return;
  firstGestureDone = true;
  ['click', 'touchend', 'keydown'].forEach(e => {
    document.removeEventListener(e, onFirstGesture, true);
  });
  try {
    M3?.resumePlayback?.();
  } catch (e) {
    Logger?.log?.({ ts: Date.now(), module: 'main', fn: 'firstGesture_resume', error: e });
  }
  // Re-arm M8 handlers now that audio is playing — ensures iOS
  // associates previoustrack/nexttrack with the active session.
  M8?.rearm?.();
}
['click', 'touchend', 'keydown'].forEach(e => {
  document.addEventListener(e, onFirstGesture, { once: false, capture: true });
});

// Initialize M8 (Media Session Bridge)
// M8._scheduleActivation also registers capture:true listeners on the
// same events. They fire AFTER onFirstGesture (registered second),
// so by then _audio exists and wakeMediaSession succeeds.
M8?.init?.();

// Wire M4 to M6
M4?.subscribe?.(M6);

// Wire M8 directly to M6 for Media Session metadata
M6?.onTrackChange?.((track) => {
  M8?.onTrackUpdate?.(track);
});

// ── Mount Svelte app (replaces M5.mount()) ────────────────────

const target = document.getElementById('app');
if (!target) {
  throw new Error('Mount target #app not found in DOM');
}

const app = new App({ target, props: { catalogError: null } });

// Wire catalog error reactively
function updateCatalogError() {
  if (catalogError !== null) {
    (app as any).$set({ catalogError });
  }
}
// Check after M7.init settles (it's async)
setTimeout(updateCatalogError, 2000);

// ── Expose for debugging and test runner ──────────────────────
(window as any).__svelteApp = app;

// Compatibility shim for test harness (checks window.M5)
(window as any).M5 = true;
